import {
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  OutlinedInput,
  Radio,
  RadioGroup,
} from '@mui/material';
import { FormFieldProps } from '.';
import { useTranslation } from 'react-i18next';
import { getFieldLabel } from '../../utils';
import {
  FieldAttributesOptions,
  FieldTemplate,
} from 'api/models/FieldTemplate';
import { GeneralIds, ModelTypes } from 'types/types';
import { useParams } from 'react-router-dom';
import useRequest from 'hooks/useRequest';
import { query } from 'operations';
import { useRecoilValue } from 'recoil';
import { fieldTemplatesState } from 'state';
import { FieldHelperText } from './FieldHelperText';

export default function RadioField({
  disabled,
  field,
  error,
  value,
  onChange,
  helperText,
  name,
  sx,
  label,
  inputProps,
  isImported,
  style
}: FormFieldProps) {
  const isReadOnly = inputProps?.readOnly;
  const { t } = useTranslation();
  const { description, optionsSourceFieldTemplateId, isMultiSelect } =
    field.fieldAttributes ?? {};
  const { projectId } = useParams<GeneralIds>();
  // only used for tracking changes so we fetch the options if they are edited within the same field
  const formsFields = useRecoilValue(fieldTemplatesState);

  const { data: options, loading } = useRequest<FieldAttributesOptions[]>(
    async () => {
      if (!optionsSourceFieldTemplateId) return field.fieldAttributes?.options;
      const { data: fields } = await query<FieldTemplate[]>({
        type: ModelTypes.fieldTemplate,
        ids: {
          projectId,
          id: optionsSourceFieldTemplateId,
        },
      });

      const optionField = fields[0];
      return optionField?.fieldAttributes.options;
    },
    {
      dependencies: [
        optionsSourceFieldTemplateId,
        field.fieldAttributes?.options,
        formsFields,
      ],
    },
  );

  return (
    <FormControl
      error={error}
      sx={{
        ...sx
      }} fullWidth
      disabled={disabled || isReadOnly}>

      <InputLabel shrink>
        {label ?? getFieldLabel({ field, t })}
      </InputLabel>

      <OutlinedInput
        label={label ?? getFieldLabel({ field, t })}
        notched
        readOnly
        error={error}
        sx={{
          cursor: 'default',
          borderRadius: 1,
          padding: '7px 14px',
          margin: 0,
          position: 'relative',
          '& .MuiOutlinedInput-input': {
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            px: 1,
            py: 1.5,
          },
        }}
        inputComponent={() => (
          <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            {isMultiSelect ? (
              <FormGroup row sx={{ p: 0 }}>
                {loading ? (
                  <CircularProgress />
                ) : (
                  options?.map(option => (
                    <FormControlLabel
                      key={`${option.label}-${option.value}-${field.id}`}
                      control={<Checkbox />}
                      label={option.label}
                      checked={(value as string[])?.includes(option.value) ?? false}
                      disabled={disabled || isReadOnly}
                      onChange={() => {
                        const selectedValues = (value as string[]) ?? [];
                        const updatedValues = selectedValues.includes(option.value)
                          ? selectedValues.filter(v => v !== option.value)
                          : [...selectedValues, option.value];
                        onChange({ target: { name, value: updatedValues } });
                      }}
                    />
                  ))
                )}
              </FormGroup>
            ) : (
              <RadioGroup row name={name} value={value} onChange={onChange} sx={{ p: 0 }}>
                {loading ? (
                  <CircularProgress />
                ) : (
                  options?.map(option => (
                    <FormControlLabel
                      onClick={e => {
                        if (option.value === value && !isReadOnly) {
                          const customEvent = {
                            target: {
                              name,
                              value: option.value === value ? null : option.value,
                            },
                          };
                          onChange(customEvent);
                        }
                      }}
                      disabled={disabled || isReadOnly}
                      key={`${option.label}-${option.value}-${field.id}`}
                      checked={value === option.value}
                      value={option.value ?? ''}
                      control={<Radio />}
                      label={option.label}
                    />
                  ))
                )}
              </RadioGroup>
            )}
          </Box>
        )}
      />
      <FieldHelperText
        description={description}
        helperText={helperText}
        error={error}
        sx={{
          paddingTop: '3px',
          paddingRight: '14px',
          paddingLeft: '14px',
        }}
      />
    </FormControl>
  );
}
